/**
 * Analysis client — spec §F6 (the dock) over §D6 (`POST /analysis/{tool}`,
 * `GET /analysis/jobs/{id}`) and Part H (the eight result schemas).
 *
 * Three things live here rather than in the dock's components:
 *
 * * **the tool registry** — Part H's table (order, icon-tile colour, tag) is a contract
 *   the cards, the tool frames and the legend's run names all read. Names, tags and
 *   descriptions are *not* here: they are prose, so they go through `t()` (§F12).
 * * **the two calls**, `runTool` and the poll, so no component builds an analysis URL.
 * * **the result types**, mirroring what the eight tools actually answer. They are written
 *   from the server's payloads in server casing (`unit_id`, `result_layers`, `share_a_pct`)
 *   for the same reason `ResultLayerSpec` is: the panels render what arrived, and a
 *   camelCase translation layer is one more place for a field to go missing silently.
 *
 * ## The poll
 *
 * §F6 is "POST → progress bar polling job → result panels", and the plan's throttle rule
 * fixes the cadence: **1.2 s, ×1.5 each tick, capped at 5 s, giving up at 120 s.** A tool
 * that answers in two seconds costs two requests; a 90-second suitability sweep over 1 048
 * communes costs eleven, not seventy-five. `PUBLIC_READ_RATE` is 600/min per IP and the
 * e2e suite is already throttle-bound (NOTES #275, #335), so a fixed 1 s poll would make
 * the dock the loudest client in the app. Sharing that throttle is also why a tick that
 * *fails* to arrive is not the end of the run: one 429 must not abandon a job the server
 * is still working on (`POLL_RETRIES`).
 *
 * Two answers never reach the poll at all:
 *
 * * a **sync** tool (§H1 compare, §H6 spi) answers `{status:'done', result}` to the POST;
 * * a **§E7 cache hit** answers `{cached:true, result}` — the same run, already computed.
 *
 * Both are short-circuited in `useToolRun`, which is why a second identical run is
 * instant and why the panel can say so.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchAnalysisArtifact, getJson, sendJson, type AnalysisJobStatus } from './api';
import type { IconName } from './icons';
import type { ResultLayerSpec } from './state';
import { toLatLon, type PointLike } from './geo';

/* ------------------------------------------------------------------ the registry */

/**
 * Part H's eight tools, in Part H's order. **The API's ids, not the mockup's** — the
 * mockup calls them `suit` and `drought`, `analysis_jobs.tool` (§C) calls them
 * `suitability` and `spi`, and the id is what the URL, the cache key and the audit row
 * carry. The mockup's *copy* still wins on screen; that lives in `analysis.tool.*`.
 */
export const TOOL_IDS = [
  'compare',
  'aoi',
  'zonal',
  'suitability',
  'change',
  'spi',
  'buffer',
  'overlay',
] as const;

export type ToolId = (typeof TOOL_IDS)[number];

export interface ToolCard {
  id: ToolId;
  /** Part H's icon-tile colour. */
  color: string;
  /** The mockup's `TOOLS[].ic`. */
  icon: IconName;
}

/** Part H's dock-card metadata table, verbatim. */
export const TOOLS: readonly ToolCard[] = [
  { id: 'compare', color: '#456082', icon: 'legend' },
  { id: 'aoi', color: '#B07818', icon: 'draw' },
  { id: 'zonal', color: '#2E7D32', icon: 'chart' },
  { id: 'suitability', color: '#8B5A2B', icon: 'layers' },
  { id: 'change', color: '#6B8E23', icon: 'swipe' },
  { id: 'spi', color: '#C77B21', icon: 'chart' },
  { id: 'buffer', color: '#2B6CB0', icon: 'draw' },
  { id: 'overlay', color: '#8E3B62', icon: 'basemap' },
];

export const toolCard = (id: ToolId): ToolCard => {
  const card = TOOLS.find((tool) => tool.id === id);
  // Unreachable for a `ToolId`, but the fallback keeps the return type honest rather than
  // pushing an optional through every card, frame and legend label.
  return card ?? { id, color: '#2E7D32', icon: 'wrench' };
};

/** Part H's `CMP` — the comparison palette, in the order §H1 assigns it. */
export const CMP = ['#2E7D32', '#2B6CB0', '#C77B21', '#8E3B62'];

/** §H1 "enforce 2–4 with toast". */
export const MIN_COMPARE_UNITS = 2;
export const MAX_COMPARE_UNITS = 4;

/* --------------------------------------------------------------------- the calls */

/** What §D6's submit answers: a job to poll, or a finished run (sync, or an §E7 hit). */
export interface ToolSubmission<T> {
  job_id: string;
  status?: AnalysisJobStatus['status'];
  cached?: boolean;
  result?: T;
  error?: string;
}

/* ------------------------------------------------- §E7's oversized-result spill */

/**
 * What is left on the row when a result does not fit (`tasks._within_size_budget`).
 *
 * §E7 caps `analysis_jobs.result` at 200 kB, because the row is what the §F6 dock polls
 * every 1.2 s per open result. Over that, the worker writes the whole result to the job's
 * artefact directory and replaces the row's copy with **six keys**: `source`,
 * `result_layers`, `geometry`, `kind`, `layer`, plus `oversized` and `result_uri`. Every
 * tool-specific field — §H3's `rows`, §H4's `criteria_used`, §H5's `matrix` — is gone.
 *
 * That is reachable in two clicks: §H3 with Zones = Communes over the whole programme
 * serialises 1 048 rows at ~196 B each, which is over the budget before the envelope is
 * counted. So the client half is not optional, and it is here rather than in the eight
 * panels: a panel dereferences `result.rows.length` while it renders, so a guard in each of
 * them is eight guards and eight chances to miss a field. `hydrated` follows the link once,
 * in the two functions every panel's result arrives through, and the panels never see a
 * summary at all (NOTES #650).
 */
export interface OversizedResult {
  oversized: true;
  result_uri: string;
}

export const isOversized = (result: unknown): result is OversizedResult =>
  typeof result === 'object' &&
  result !== null &&
  (result as { oversized?: unknown }).oversized === true &&
  typeof (result as { result_uri?: unknown }).result_uri === 'string';

/**
 * The sentinel error a spilled result the client could not fetch carries.
 *
 * A sibling of `TIMED_OUT`, for the same reason: the panel translates it (`ToolFrame`)
 * rather than printing a sentence this module invented in one language. The reader gets
 * "this result is too large to show here — download it" instead of a blank page, which is
 * what dereferencing the summary produced before this existed.
 */
export const OVERSIZED = '__oversized__';

/** Follow `result_uri` when the row holds only §E7's summary. Never rejects. */
async function hydrated<T>(result: T | undefined): Promise<T | undefined | null> {
  if (!isOversized(result)) return result;
  try {
    return await fetchAnalysisArtifact<T>(result.result_uri);
  } catch {
    // The artefact is reaped at 7 days (`apps.analysis.reaper`) and the link is re-gated on
    // every fetch, so "gone" and "not yours" are both ordinary answers here. Neither is a
    // reason to hand the panel a result with no rows in it.
    return null;
  }
}

/**
 * §D6 `POST /analysis/{tool}`. The body **is** the tool's params — §E7 canonicalises and
 * hashes it, so anything added here changes the cache key.
 *
 * A **sync** tool and an §E7 cache hit answer with the result on the POST, so the spill can
 * arrive here as well as on the poll; both paths hydrate.
 */
export const runTool = async <T>(
  tool: ToolId,
  params: Record<string, unknown>,
): Promise<ToolSubmission<T>> => {
  const submission = (await sendJson<ToolSubmission<T>>(
    `/analysis/${tool}`,
    params,
  )) as ToolSubmission<T>;
  if (!isOversized(submission.result)) return submission;
  const full = await hydrated<T>(submission.result);
  return full === null
    ? { ...submission, status: 'error', result: undefined, error: OVERSIZED }
    : { ...submission, result: full };
};

/** §D6 `GET /analysis/jobs/{id}` — deliberately uncached (the answer changes per tick). */
export const fetchJob = async <T>(jobId: string): Promise<AnalysisJobStatus<T>> => {
  const job = await getJson<AnalysisJobStatus<T>>(
    `/analysis/jobs/${encodeURIComponent(jobId)}`,
    { credentials: 'same-origin' },
  );
  if (!isOversized(job.result)) return job;
  const full = await hydrated<T>(job.result);
  return full === null
    ? { ...job, status: 'error', result: undefined, error: OVERSIZED }
    : { ...job, result: full };
};

/* ---------------------------------------------------------------------- the poll */

/** First tick after the submit. */
export const POLL_START_MS = 1200;
/** Backoff factor — see the module header. */
export const POLL_FACTOR = 1.5;
/** The slowest the dock ever polls. */
export const POLL_MAX_MS = 5000;
/** After this the dock stops asking and says so; the job itself carries on server-side. */
export const POLL_TIMEOUT_MS = 120_000;

/**
 * How many **consecutive** failed poll requests the dock rides out before it gives up.
 *
 * A tick that never arrives says nothing about the job: the poll shares the app's 600/min
 * `PUBLIC_READ_RATE`, so a burst elsewhere in the page can answer one tick with a 429, and
 * a proxy restart answers with a 502. Ending the run on the first of those abandons a job
 * that is still running server-side and prints a transport message where §D6's
 * reader-facing sentence belongs. The next tick backs off anyway, which is the right
 * response to both causes, so it is allowed to happen (NOTES #639).
 */
export const POLL_RETRIES = 1;

export const nextDelay = (delay: number): number =>
  Math.min(POLL_MAX_MS, Math.round(delay * POLL_FACTOR));

export type RunPhase = 'idle' | 'running' | 'done' | 'error';

export interface JobState<T> {
  phase: RunPhase;
  progress: number;
  result: T | null;
  error: string | null;
}

const IDLE: JobState<never> = { phase: 'idle', progress: 0, result: null, error: null };

/**
 * Poll one job to completion.
 *
 * `jobId` is the switch: `null` is idle, a new id starts a fresh poll, and unmounting (or
 * clearing the id) stops the timer — a timer that outlives its panel is a leak that keeps
 * hitting a throttled endpoint.
 *
 * The deadline is measured from the moment the id arrives, not from the first tick, so a
 * slow first response does not buy the job extra time.
 */
export function useAnalysisJob<T>(jobId: string | null): JobState<T> {
  const [state, setState] = useState<JobState<T>>(IDLE as JobState<T>);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!jobId) {
      setState(IDLE as JobState<T>);
      return;
    }
    let alive = true;
    let fails = 0;
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    setState({ phase: 'running', progress: 0, result: null, error: null });

    const tick = (delay: number) => {
      timer.current = setTimeout(() => {
        void fetchJob<T>(jobId)
          .then((job) => {
            if (!alive) return;
            fails = 0;
            if (job.status === 'done') {
              setState({
                phase: 'done',
                progress: 100,
                result: (job.result ?? null) as T | null,
                error: null,
              });
              return;
            }
            if (job.status === 'error') {
              setState({
                phase: 'error',
                progress: job.progress ?? 0,
                result: null,
                error: job.error ?? null,
              });
              return;
            }
            setState((prev) => ({ ...prev, progress: job.progress ?? prev.progress }));
            if (Date.now() > deadline) {
              setState((prev) => ({ ...prev, phase: 'error', error: TIMED_OUT }));
              return;
            }
            tick(nextDelay(delay));
          })
          .catch((err: unknown) => {
            if (!alive) return;
            fails += 1;
            // A transport failure is a *skipped* tick, not a failed job — up to a point.
            // See `POLL_RETRIES`.
            if (fails <= POLL_RETRIES && Date.now() <= deadline) {
              tick(nextDelay(delay));
              return;
            }
            setState((prev) => ({
              ...prev,
              phase: 'error',
              result: null,
              error: Date.now() > deadline ? TIMED_OUT : err instanceof Error ? err.message : null,
            }));
          });
      }, delay);
    };
    tick(POLL_START_MS);

    return () => {
      alive = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [jobId]);

  return state;
}

/**
 * The sentinel `error` a give-up carries, so the panel can translate it rather than print
 * an English sentence this module invented. Every other `error` on this state is the
 * server's own reader-facing message (§D6), which is already localised by `?lang=`.
 */
export const TIMED_OUT = '__timeout__';

export interface ToolRun<T> extends JobState<T> {
  /** True when this answer came out of the §E7 params-hash cache. */
  cached: boolean;
  jobId: string | null;
  run: (params: Record<string, unknown>) => void;
  reset: () => void;
}

/**
 * Submit a tool and follow it to an answer — what all eight forms use.
 *
 * The submit's own reply decides whether there is anything to poll: a sync tool and a
 * cache hit both arrive finished, and starting a poll for them would be one wasted
 * request against a 30/min endpoint and a progress bar that flashed for nothing.
 *
 * **Only the newest run may write.** `seq` is bumped on every `run()` and every callback
 * checks it before touching state, because two submits can be in flight at once and they
 * do not have to come back in order. §H2 makes that ordinary rather than exotic: the card
 * fires on `(aoi.id, layer_id)` the moment it opens, so changing the focus select while
 * the first answer is still on the wire is two runs — and without this guard the *older*
 * reply wins whenever it lands second. Its `.then` wrote a finished `immediate`, which
 * takes precedence over the newer run's poll in `state` below, so the panel showed the
 * wrong raster's numbers under the right raster's name, permanently. Found by
 * `e2e/m4.spec.ts`'s §H2 clause on a loaded machine (NOTES #647).
 */
export function useToolRun<T>(tool: ToolId): ToolRun<T> {
  const [jobId, setJobId] = useState<string | null>(null);
  const [immediate, setImmediate] = useState<JobState<T> | null>(null);
  const [cached, setCached] = useState(false);
  const polled = useAnalysisJob<T>(jobId);
  const alive = useRef(true);
  /** Which run is current; a callback from an older one is dropped. */
  const seq = useRef(0);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const reset = useCallback(() => {
    // Bumped here too: a `reset()` while a submit is in flight must not be undone by it.
    seq.current += 1;
    setJobId(null);
    setImmediate(null);
    setCached(false);
  }, []);

  const run = useCallback(
    (params: Record<string, unknown>) => {
      seq.current += 1;
      const mine = seq.current;
      const current = () => alive.current && seq.current === mine;
      setJobId(null);
      setCached(false);
      setImmediate({ phase: 'running', progress: 0, result: null, error: null });
      void runTool<T>(tool, params)
        .then((submission) => {
          if (!current()) return;
          setCached(Boolean(submission.cached));
          // A finished answer — sync tool or §E7 hit. `status === 'done'` and `cached`
          // are two routes to the same place; either way there is no job to watch.
          if (submission.result && (submission.cached || submission.status === 'done')) {
            setImmediate({
              phase: 'done',
              progress: 100,
              result: submission.result,
              error: null,
            });
            return;
          }
          if (submission.status === 'error') {
            setImmediate({
              phase: 'error',
              progress: 0,
              result: null,
              error: submission.error ?? null,
            });
            return;
          }
          setImmediate(null);
          setJobId(submission.job_id);
        })
        .catch((err: unknown) => {
          if (!current()) return;
          setImmediate({
            phase: 'error',
            progress: 0,
            result: null,
            error: err instanceof Error ? err.message : null,
          });
        });
    },
    [tool],
  );

  const state = immediate ?? (jobId ? polled : (IDLE as JobState<T>));
  return { ...state, cached, jobId, run, reset };
}

/* ------------------------------------------------------------------- the geometry */

/**
 * The store's AOI ring as the API wants it: **`[lon, lat]`**.
 *
 * `state.aoi.pts` is `[lat, lon]` — Leaflet's order, which the draw tool, the permalink
 * and `lib/geo.ts` all speak — and every §E7 geometry parameter is GeoJSON's, which is the
 * other way round (`geometry.ring_of`). One conversion, at the one boundary where the two
 * conventions meet, so no form has to remember (NOTES #630).
 */
export function aoiRing(pts: readonly PointLike[]): [number, number][] {
  return pts.map((p) => {
    const [lat, lon] = toLatLon(p);
    return [lon, lat] as [number, number];
  });
}

/* ------------------------------------------------------- Part H result schemas */

/** Every tool's envelope: what it computed from, and what it wants drawn (§E7). */
export interface ResultSource {
  tool: string;
  inputs?: { layer: string; dataset: string; version: string }[];
  engine?: string;
  extent?: ExtentDescription;
  [key: string]: unknown;
}

export interface ExtentDescription {
  kind: string;
  id: string | null;
  label: string;
}

export interface ToolResult {
  source: ResultSource;
  result_layers: ResultLayerSpec[];
  notes?: string[];
}

/** The trimmed raster/vector header §H2/§H3/§H5 name a layer with. */
export interface LayerDescriptor {
  id: string;
  name: string;
  unit: string | null;
  dataset: string;
  version: string;
  is_categorical?: boolean;
}

/* §H1 ------------------------------------------------------------------ compare */

export interface CompareSeries {
  unit_id: string;
  name: string;
  country: string | null;
  color: string;
  /** One per year; `null` is a gap and the chart draws it as one. */
  values: (number | null)[];
}

export interface CompareRow {
  indicator_id: string;
  code: string | null;
  name: string;
  unit: string;
  fmt: number;
  values: Record<string, number | null>;
}

export interface CompareResult extends ToolResult {
  indicator: {
    id: string;
    code: string | null;
    name: string;
    unit: string;
    fmt: number;
    is_cumulative: boolean;
  };
  mode: 'annual' | 'cumulative';
  year: number;
  years: number[];
  series: CompareSeries[];
  contractual_table: CompareRow[];
}

/* §H2 ---------------------------------------------------------------------- aoi */

export interface AoiGeometry {
  area_km2: number;
  perimeter_km: number;
  units_intersected: { regions: number; communes: number };
}

export interface AoiContinuous {
  kind: 'continuous';
  stats: { mean: number | null; min: number | null; max: number | null; std: number | null; pixels: number };
  sampled: boolean;
  resolution_m: number | null;
  benchmark: { programme_mean: number | null; delta_pct: number | null } | null;
  histogram: { bins: number[]; edges: number[] } | null;
  shares: { low: number; med: number; high: number } | null;
  series?: { year: number; mean: number | null }[];
}

export interface AoiClass {
  class: number;
  label: string;
  color: string;
  pct: number;
  km2: number;
}

export interface AoiCategorical {
  kind: 'categorical';
  composition: AoiClass[];
  pixels: number;
  sampled: boolean;
  resolution_m: number | null;
  change_vs?: {
    epoch: number | null;
    layer: LayerDescriptor;
    rows: { class: number; label: string; color: string; delta_pct: number; km2: number }[];
  } | null;
}

export type AoiResult = ToolResult &
  (AoiContinuous | AoiCategorical) & {
    geometry: AoiGeometry;
    layer: LayerDescriptor;
  };

/* §H3 -------------------------------------------------------------------- zonal */

export interface ZonalRow {
  unit_id: string | null;
  name: string;
  country: string | null;
  pixels: number;
  sampled: boolean;
  [stat: string]: unknown;
}

export interface ZonalResult extends ToolResult {
  rows: ZonalRow[];
  layer: LayerDescriptor;
  /** Column order for the table and the CSV. */
  stats: string[];
  resolution_m: number | null;
  n_zones: number;
}

/* §H4 --------------------------------------------------------------- suitability */

export interface SuitabilityRow {
  unit_id: string;
  name: string;
  country: string | null;
  score: number | null;
  /** Normalised 0–1 per criterion; `null` where the unit had no data for it. */
  criteria: Record<string, number | null>;
}

export interface CriterionUsed {
  key: string;
  label: string;
  layer: string;
  dataset: string;
  version: string;
  versions?: string[];
  resolution_m: number | null;
  direction: 'direct' | 'inverse';
  /** The **run's** weight, not the default (T5 fix round 2). */
  weight: number;
}

export interface SuitabilityResult extends ToolResult {
  rows: SuitabilityRow[];
  criteria_used: CriterionUsed[];
  weights: Record<string, number>;
  weight_total: number;
  n_units: number;
}

/* §H5 ------------------------------------------------------------------- change */

export interface ChangeClass {
  value: number;
  label: string;
  color: string;
}

export interface ChangeNetRow {
  class: number;
  label: string;
  color: string;
  delta_pct: number;
  km2: number;
  from_pct: number;
  to_pct: number;
}

export interface ChangeResult extends ToolResult {
  matrix: { classes: ChangeClass[]; rows: number[][] };
  net: ChangeNetRow[];
  extent: ExtentDescription;
  coverage: {
    pixels: number;
    unclassified_pixels: number;
    analysed_km2: number;
    extent_km2: number;
    pct: number;
  };
  epochs: { a: LayerDescriptor & { year: number | null }; b: LayerDescriptor & { year: number | null } };
  resolution_m: number | null;
  n_classes: number;
}

/* §H6 ---------------------------------------------------------------------- spi */

/** One year's class. All three fields are `null` for a year the series has no value for. */
export interface SpiClass {
  year: number;
  key: string | null;
  label: string | null;
  color: string | null;
}

export interface SpiResult extends ToolResult {
  series: { year: number; spi3: number | null }[];
  classification: SpiClass[];
  current: { year: number; value: number; key: string; label: string; color: string } | null;
  scale_months: number;
  years: number[];
  unit: { id: string; name: string; country: string | null; type: string; level: number | null };
  indicator: { id: string; name: string; unit: string; fmt: number };
}

/* §H7 ------------------------------------------------------------------- buffer */

export interface BufferResult extends ToolResult {
  n_sites: number;
  n_features: number;
  buffered_km2: number;
  features_within: { settlements: number; sites: number };
  distance_km: number;
  dissolve: boolean;
  geojson_uri: string | null;
  layer: LayerDescriptor;
  extent: ExtentDescription;
}

/* §H8 ------------------------------------------------------------------ overlay */

export interface OverlayResult extends ToolResult {
  op: string;
  n_features: number;
  overlap_km2: number;
  overlap_km: number;
  share_a_pct: number | null;
  measure: 'count' | 'length_km' | 'area_km2';
  dimension: string;
  distance_km: number | null;
  flagged_units: string[];
  n_flagged: number;
  flag_level: number;
  derived_dataset_id: string;
  derived: {
    id: string;
    title: string;
    classification: string;
    is_provisional: boolean;
    version: string;
    rows: number;
    lineage?: unknown;
  };
  inputs: { a: Record<string, unknown>; b: Record<string, unknown> };
  extent: ExtentDescription;
  geojson_uri: string | null;
}
